import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TimesheetEntryHistory, Visit } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { Alert, Button, ConfirmButton, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { StatusPill, TimeInput, isTimeOfDay } from '../../components/phase2';
import { Dialog } from '../fields-notices/Dialog';
import { reportsKey, useGroupMembers } from './api';
import { localToUtcIso, todayIn } from '../../lib/zoneTime';
import { useSite } from '../../lib/site';

/** A time typed by a person may be a moment ahead of the server clock; further ahead than this is "in the future". */
const FUTURE_SLACK_MS = 60_000;

export interface LocalStamp {
  date: string;
  time: string;
}

/** What the edit dialog needs to know about one record, in the sheet's time zone. */
export interface EditTarget {
  visitId: string;
  personName: string;
  siteName: string;
  signedIn: LocalStamp;
  /** Null while the person is still signed in. */
  signedOut: LocalStamp | null;
}

function StampFields({
  legend,
  value,
  onChange,
  error,
  required = true,
  disabled,
}: {
  legend: string;
  value: LocalStamp;
  onChange: (v: LocalStamp) => void;
  error?: string | undefined;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <fieldset disabled={disabled} className="min-w-0">
      <legend className="mb-1 text-sm font-medium text-slate-800">{legend}</legend>
      <div className="flex flex-wrap items-start gap-3">
        <TextField label={t('rpt.entry.date')} type="date" required={required} value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} />
        <TimeInput label={t('rpt.entry.time')} value={value.time} onChange={(time) => onChange({ ...value, time })} />
      </div>
      {error ? <p className="mt-1 text-sm text-red-700">{error}</p> : null}
    </fieldset>
  );
}

const stampValid = (s: LocalStamp): boolean => s.date !== '' && isTimeOfDay(s.time);
const stampBlank = (s: LocalStamp): boolean => s.date === '' && s.time === '';

function History({ visitId, zone }: { visitId: string; zone: string }) {
  const q = useQuery({
    queryKey: [...reportsKey, 'entry-history', visitId],
    queryFn: () => api.get(`/reports/timesheet/entries/${visitId}/history`, { schema: TimesheetEntryHistory }),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const stamp = (iso: string | null) => (iso ? formatDateTime(iso, zone) : t('rpt.entry.stillIn'));
  return (
    <section aria-labelledby={`hist-${visitId}`}>
      <h3 id={`hist-${visitId}`} className="mb-2 text-sm font-semibold text-slate-900">
        {t('rpt.entry.history')}
      </h3>
      <ol className="space-y-2">
        {q.data.events.map((e, i) => (
          <li key={`${e.kind}-${e.at}-${i}`} className="rounded-md border border-slate-200 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-900">{t(`rpt.entry.kind.${e.kind}`)}</span>
              <span className="text-slate-600">{formatDateTime(e.at, zone)}</span>
              {e.actorLabel ? <span className="text-slate-600">{t('rpt.entry.by', { name: e.actorLabel })}</span> : null}
              {!e.applied ? <StatusPill tone="neutral">{t('rpt.entry.superseded')}</StatusPill> : null}
            </div>
            {e.before || e.after ? (
              <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
                {e.before ? (
                  <>
                    <dt className="text-slate-600">{t('rpt.entry.before')}</dt>
                    <dd>{t('rpt.entry.range', { from: stamp(e.before.signedInAt), to: stamp(e.before.signedOutAt) })}</dd>
                  </>
                ) : null}
                {e.after ? (
                  <>
                    <dt className="text-slate-600">{t('rpt.entry.after')}</dt>
                    <dd>{t('rpt.entry.range', { from: stamp(e.after.signedInAt), to: stamp(e.after.signedOutAt) })}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}
            {e.reason ? <p className="mt-2 text-slate-700">{t('rpt.entry.reason', { reason: e.reason })}</p> : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** One timesheet record: its times (editable with `timesheets.edit`), delete (also needs `history.delete`) and its full history. */
export function EditEntryDialog({
  target,
  zone,
  canEdit,
  canDelete,
  onClose,
}: {
  target: EditTarget;
  zone: string;
  canEdit: boolean;
  canDelete: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [signedIn, setSignedIn] = useState<LocalStamp>(target.signedIn);
  const [signedOut, setSignedOut] = useState<LocalStamp>(target.signedOut ?? { date: '', time: '' });
  const [reason, setReason] = useState('');

  const originalOut: LocalStamp = target.signedOut ?? { date: '', time: '' };
  const inChanged = signedIn.date !== target.signedIn.date || signedIn.time !== target.signedIn.time;
  const outChanged = signedOut.date !== originalOut.date || signedOut.time !== originalOut.time;
  const stillIn = stampBlank(signedOut);
  const inOk = stampValid(signedIn);
  const outOk = stillIn || stampValid(signedOut);
  const inIso = inOk ? localToUtcIso(signedIn.date, signedIn.time, zone) : '';
  const outIso = !stillIn && stampValid(signedOut) ? localToUtcIso(signedOut.date, signedOut.time, zone) : '';
  const orderBad = inOk && outIso !== '' && outIso <= inIso;
  const futureBad = (iso: string) => iso !== '' && new Date(iso).getTime() > Date.now() + FUTURE_SLACK_MS;
  const inFuture = futureBad(inIso);
  const outFuture = futureBad(outIso);
  const valid = (inChanged || outChanged) && inOk && outOk && !orderBad && !inFuture && !outFuture && reason.trim() !== '';

  const done = () => {
    void qc.invalidateQueries({ queryKey: reportsKey });
    onClose();
  };
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/reports/timesheet/entries/${target.visitId}`, {
        body: {
          ...(inChanged ? { signedInAt: inIso } : {}),
          ...(outChanged ? { signedOutAt: stillIn ? null : outIso } : {}),
          reason: reason.trim(),
        },
        schema: Visit,
      }),
    onSuccess: () => {
      toast(t('rpt.entry.saved'));
      done();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/reports/timesheet/entries/${target.visitId}`, { body: { reason: reason.trim() } }),
    onSuccess: () => {
      toast(t('rpt.entry.deleted'));
      done();
    },
  });
  const errs = fieldErrors(save.error);

  return (
    <Dialog title={t('rpt.entry.editTitle', { name: target.personName })} onClose={onClose} wide>
      <div className="space-y-5">
        <p className="text-sm text-slate-600">{t('rpt.entry.site', { site: target.siteName, zone })}</p>
        {canEdit ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid) save.mutate();
            }}
          >
            <StampFields legend={t('rpt.entry.signedIn')} value={signedIn} onChange={setSignedIn} error={errs['signedInAt'] ?? (inFuture ? t('rpt.entry.future') : undefined)} />
            <StampFields
              legend={t('rpt.entry.signedOut')}
              value={signedOut}
              required={false}
              onChange={setSignedOut}
              error={errs['signedOutAt'] ?? (orderBad ? t('rpt.entry.order') : outFuture ? t('rpt.entry.future') : undefined)}
            />
            <p className="text-xs text-slate-600">{t('rpt.entry.leaveBlank')}</p>
            <TextField label={t('rpt.entry.reasonLabel')} hint={t('rpt.entry.reasonHint')} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
            <ErrorLine error={save.error ?? remove.error} />
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" variant="primary" disabled={!valid || save.isPending}>
                {t('common.save')}
              </Button>
              {canDelete ? (
                <ConfirmButton
                  label={t('rpt.entry.delete')}
                  disabled={reason.trim() === '' || remove.isPending}
                  pending={remove.isPending}
                  onConfirm={() => remove.mutate()}
                />
              ) : null}
              <Button onClick={onClose}>{t('common.cancel')}</Button>
            </div>
            {canDelete && reason.trim() === '' ? <p className="text-xs text-slate-600">{t('rpt.entry.deleteNeedsReason')}</p> : null}
          </form>
        ) : (
          <dl className="grid gap-1 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
            <dt className="text-slate-600">{t('rpt.entry.signedIn')}</dt>
            <dd>{`${target.signedIn.date} ${target.signedIn.time}`}</dd>
            <dt className="text-slate-600">{t('rpt.entry.signedOut')}</dt>
            <dd>{target.signedOut ? `${target.signedOut.date} ${target.signedOut.time}` : t('rpt.entry.stillIn')}</dd>
          </dl>
        )}
        <History visitId={target.visitId} zone={zone} />
      </div>
    </Dialog>
  );
}

/** Add a record for a person who forgot to sign in or out. Times are the chosen site's local time. */
export function AddEntryDialog({ groupId, defaultSiteId, zone, onClose }: { groupId: string; defaultSiteId: string; zone: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { sites } = useSite();
  const members = useGroupMembers(groupId);
  const [memberId, setMemberId] = useState('');
  const [siteId, setSiteId] = useState(defaultSiteId || (sites[0]?.id ?? ''));
  const today = todayIn(zone);
  const [signedIn, setSignedIn] = useState<LocalStamp>({ date: today, time: '09:00' });
  const [signedOut, setSignedOut] = useState<LocalStamp>({ date: today, time: '17:00' });
  const [reason, setReason] = useState('');

  const siteZone = sites.find((s) => s.id === siteId)?.timeZone ?? zone;
  const ok = stampValid(signedIn) && stampValid(signedOut);
  const inIso = ok ? localToUtcIso(signedIn.date, signedIn.time, siteZone) : '';
  const outIso = ok ? localToUtcIso(signedOut.date, signedOut.time, siteZone) : '';
  const orderBad = ok && outIso <= inIso;
  const future = ok && new Date(outIso).getTime() > Date.now() + FUTURE_SLACK_MS;
  const valid = ok && memberId !== '' && siteId !== '' && !orderBad && !future && reason.trim() !== '';

  const create = useMutation({
    mutationFn: () => api.post('/reports/timesheet/entries', { body: { memberId, siteId, signedInAt: inIso, signedOutAt: outIso, reason: reason.trim() }, schema: Visit }),
    onSuccess: () => {
      toast(t('rpt.entry.added'));
      void qc.invalidateQueries({ queryKey: reportsKey });
      onClose();
    },
  });
  const errs = fieldErrors(create.error);

  return (
    <Dialog title={t('rpt.entry.addTitle')} onClose={onClose} wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField label={t('rpt.entry.person')} value={memberId} onChange={(e) => setMemberId(e.target.value)} required>
            <option value="">{t('rpt.entry.choosePerson')}</option>
            {(members.data?.items ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.fullName}
              </option>
            ))}
          </SelectField>
          <SelectField label={t('rpt.entry.siteLabel')} value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
        </div>
        <p className="text-xs text-slate-600">{t('rpt.entry.zoneNote', { zone: siteZone })}</p>
        <StampFields legend={t('rpt.entry.signedIn')} value={signedIn} onChange={setSignedIn} error={errs['signedInAt']} />
        <StampFields legend={t('rpt.entry.signedOut')} value={signedOut} onChange={setSignedOut} error={errs['signedOutAt'] ?? (orderBad ? t('rpt.entry.order') : future ? t('rpt.entry.future') : undefined)} />
        <TextField label={t('rpt.entry.reasonLabel')} hint={t('rpt.entry.reasonHint')} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
        {members.error ? <Alert>{t('rpt.entry.membersFailed')}</Alert> : null}
        <ErrorLine error={create.error} />
        <div className="flex gap-3">
          <Button type="submit" variant="primary" disabled={!valid || create.isPending}>
            {t('rpt.entry.add')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
